import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SessionProvider, useSession } from './lib/session';
import { defaultSlug } from './lib/business';
import { LiveRegions } from './shell/LiveRegions';
import { EventsBridge } from './shell/EventsBridge';
import { ToastViewport } from './ui/Toast';
import { ClientShell } from './shell/ClientShell';
import { StaffShell } from './shell/StaffShell';
import { RouteError, PageFallback } from './shell/RouteError';
import { ApiError } from './lib/api';
import { getOwnerEpoch, ownerKeyHash } from './lib/owner';
import { OwnerScope } from './lib/ownerScope';
import { BrandingBridge } from './lib/branding';
import { ImpersonationBanner, ImpersonationController } from './shell/ImpersonationBanner';

// Lazy route chunks. File names matter: .size-limit.cjs budgets `route-login-*` and `route-client-home-*`.
const Setup = lazy(() => import('./routes/route-setup'));
const Login = lazy(() => import('./routes/route-login'));
const ClientHome = lazy(() => import('./routes/route-client-home'));
const Requests = lazy(() => import('./routes/client/Requests'));
const Composer = lazy(() => import('./routes/client/Composer'));
const OrderPage = lazy(() => import('./routes/client/OrderPage'));
const Confirmation = lazy(() => import('./routes/client/Confirmation'));
const TaskPage = lazy(() => import('./routes/client/TaskPage'));
const Messages = lazy(() => import('./routes/client/Messages'));
const MePage = lazy(() => import('./routes/MePage'));
const StaffOverview = lazy(() => import('./routes/staff/route-staff-overview'));
const StaffInbox = lazy(() => import('./routes/staff/route-staff-inbox'));
const StaffBoard = lazy(() => import('./routes/staff/route-staff-board'));
const StaffSchedule = lazy(() => import('./routes/staff/route-staff-schedule'));
const StaffTask = lazy(() => import('./routes/staff/route-staff-task'));
const StaffBusinesses = lazy(() => import('./routes/staff/route-staff-businesses'));
const StaffMore = lazy(() => import('./routes/staff/route-staff-more'));
// Admin console is T12 (@gpt): it owns everything under routes/admin/.
const Admin = lazy(() => import('./routes/admin/route-admin'));

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Every cached query is scoped to the signed-in user (§11c, lib/owner.ts).
      queryKeyHashFn: ownerKeyHash,
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (n, e) => !(e instanceof ApiError && e.status >= 400 && e.status < 500) && n < 2,
    },
  },
});

const page = (el: ReactNode) => <Suspense fallback={<PageFallback />}>{el}</Suspense>;

export function RequireAuth({ staff, children }: { staff?: boolean; children: ReactNode }) {
  const { me, loading } = useSession();
  const loc = useLocation();
  if (loading) return <PageFallback full />;
  if (!me) return <Navigate to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  if (staff && me.role === 'client') return <Navigate to="/" replace />;
  if (staff === false && me.role !== 'client') return <Navigate to="/staff" replace />;
  // Keyed by account and owner epoch: an account change remounts the whole authenticated tree, so no
  // component-local state (drafts, rows, thread bodies, pending sends) survives into the next account.
  return <OwnerScope key={`${me.id}:${getOwnerEpoch()}`}>{children}</OwnerScope>;
}

function Home() {
  const { me, loading } = useSession();
  if (loading) return <PageFallback full />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.role !== 'client') return <Navigate to="/staff" replace />;
  const slug = defaultSlug(me);
  return <Navigate to={slug ? `/b/${slug}` : '/no-access'} replace />;
}

function MeRedirect() {
  const { me, loading } = useSession();
  if (loading) return <PageFallback full />;
  if (!me) return <Navigate to="/login" replace />;
  if (me.role !== 'client') return <Navigate to="/staff/me" replace />;
  return <Navigate to={`/b/${defaultSlug(me) ?? 'all'}/me`} replace />;
}

function Root() {
  return (
    <SessionProvider>
      <BrandingBridge />
      <EventsBridge />
      <ImpersonationController />
      <Outlet />
      <ToastViewport />
    </SessionProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { path: '/', element: <Home /> },
      { path: '/login', element: page(<Login />) },
      { path: '/setup', element: page(<Setup />) },
      { path: '/me', element: <MeRedirect /> },
      {
        path: '/no-access',
        element: page(<NoAccess />),
      },
      {
        path: '/b/:slug',
        element: (
          <RequireAuth staff={false}>
            <ClientShell />
          </RequireAuth>
        ),
        errorElement: <RouteError />,
        children: [
          { index: true, element: page(<ClientHome />) },
          { path: 'requests', element: page(<Requests />) },
          { path: 'new', element: page(<Composer />) },
          { path: 'orders/:orderId', element: page(<OrderPage />) },
          { path: 'orders/:orderId/edit', element: page(<Composer />) },
          { path: 'orders/:orderId/submitted', element: page(<Confirmation />) },
          { path: 'tasks/:taskId', element: page(<TaskPage />) },
          { path: 'messages', element: page(<Messages />) },
          { path: 'messages/:threadId', element: page(<Messages />) },
          { path: 'me', element: page(<MePage />) },
        ],
      },
      {
        path: '/staff',
        element: (
          <RequireAuth staff>
            <StaffShell />
          </RequireAuth>
        ),
        errorElement: <RouteError />,
        children: [
          { index: true, element: page(<StaffOverview />) },
          { path: 'inbox', element: page(<StaffInbox />) },
          { path: 'board', element: page(<StaffBoard />) },
          { path: 'schedule', element: page(<StaffSchedule />) },
          { path: 'tasks/:taskId', element: page(<StaffTask />) },
          { path: 'businesses', element: page(<StaffBusinesses />) },
          { path: 'more', element: page(<StaffMore />) },
          { path: 'me', element: page(<MePage />) },
        ],
      },
      {
        path: '/admin/*',
        element: <RequireAuth staff>{page(<Admin />)}</RequireAuth>,
        errorElement: <RouteError />,
      },
      { path: '*', element: <RouteError notFound /> },
    ],
  },
]);

function NoAccess() {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-3 p-6 text-center">
      <ImpersonationBanner compact className="text-left" />
      <h1 className="text-2xl font-semibold">No business linked yet</h1>
      <p className="text-muted">Your account isn't linked to a business. Ask your team to add you.</p>
    </main>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <LiveRegions />
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
